import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("pt-BR"); }
  catch { return iso; }
}
function fmtMoney(v) {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function StatusPagamento({ s }) {
  const colors = {
    pago: "bg-[#00A859] text-white",
    pendente: "bg-yellow-500 text-white",
    cancelado: "bg-zinc-400 text-white",
    atrasado: "bg-[#E60000] text-white",
  };
  return <span className={`px-2 py-0.5 text-[10px] ${colors[s] || "bg-secondary"}`} data-testid={`pag-${s}`}>{s}</span>;
}

function exportCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(";"),
    ...rows.map(r => headers.map(h => `"${String(r[h] ?? "").replace(/"/g, '""')}"`).join(";")),
  ].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function Relatorios() {
  return (
    <div className="p-8">
      <div className="mb-6">
        <div className="label-cap">MontaJá · Relatórios</div>
        <h1 className="font-heading font-extrabold text-3xl tracking-tighter mt-1">Relatórios da Empresa</h1>
        <p className="text-xs text-muted-foreground mt-1">Produção, fechamentos e indicadores financeiros — exportáveis em CSV.</p>
      </div>

      <Tabs defaultValue="producao">
        <TabsList className="rounded-none">
          <TabsTrigger value="producao" data-testid="rel-tab-producao">Produção por montador</TabsTrigger>
          <TabsTrigger value="fechamentos" data-testid="rel-tab-fechamentos">Fechamentos</TabsTrigger>
        </TabsList>

        <TabsContent value="producao" className="mt-4">
          <ProducaoMontadores />
        </TabsContent>
        <TabsContent value="fechamentos" className="mt-4">
          <Fechamentos />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function ProducaoMontadores() {
  const { user } = useAuth();
  const [dias, setDias] = useState(90);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/relatorios/producao-por-montador?dias=${dias}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dias]);

  useEffect(load, [load]);

  const podeVerPIX = user?.role === "admin" || user?.role === "empresa";

  return (
    <>
      <div className="flex justify-between items-end mb-4 gap-3">
        <div className="flex gap-2">
          {[30, 60, 90, 180, 365].map(d => (
            <button key={d} onClick={() => setDias(d)}
              className={`btn-sharp px-3 py-1.5 text-xs border ${dias === d ? "brand-bg text-white border-transparent" : "border-border bg-white"}`}
              data-testid={`rel-prod-dias-${d}`}>
              {d}d
            </button>
          ))}
        </div>
        <Button variant="outline" className="btn-sharp text-xs"
          onClick={() => exportCsv(`producao-montadores-${dias}d.csv`, data?.resultados || [])}
          disabled={!data?.resultados?.length}
          data-testid="rel-prod-export">
          Exportar CSV
        </Button>
      </div>

      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <Th>Montador</Th><Th>Cidade</Th>
              <Th>Montagens</Th><Th>Assist.</Th><Th>Revisões</Th><Th>Trocas</Th><Th>Total</Th>
              <Th>Valor gerado</Th><Th>Valor pago</Th>
              {podeVerPIX && <Th>PIX</Th>}
              <Th>Score</Th><Th>Retorno 30d</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="p-8 text-center text-muted-foreground">Carregando…</td></tr>
            ) : !data?.resultados?.length ? (
              <tr><td colSpan={12} className="p-8 text-center text-muted-foreground">Nenhum serviço finalizado no período.</td></tr>
            ) : data.resultados.map(r => (
              <tr key={r.montador_id} className="border-t border-border hover:bg-secondary/30" data-testid={`prod-${r.montador_id}`}>
                <Td><strong>{r.nome}</strong>{r.cpf_cnpj && <div className="text-[10px] text-muted-foreground font-mono">{r.cpf_cnpj}</div>}</Td>
                <Td>{r.cidade || "—"}</Td>
                <Td className="text-right font-mono">{r.qtd_montagem}</Td>
                <Td className="text-right font-mono">{r.qtd_assistencia}</Td>
                <Td className="text-right font-mono">{r.qtd_revisao}</Td>
                <Td className="text-right font-mono">{r.qtd_troca}</Td>
                <Td className="text-right font-mono font-bold">{r.qtd_total}</Td>
                <Td className="text-right font-mono">{fmtMoney(r.valor_total_gerado)}</Td>
                <Td className="text-right font-mono">{fmtMoney(r.valor_total_pago)}</Td>
                {podeVerPIX && <Td className="text-xs font-mono">{r.pix || "—"}</Td>}
                <Td className="text-center">{Math.round(r.score || 0)}</Td>
                <Td className={`text-right font-mono ${r.indice_retorno_30d_pct > 10 ? "text-[#E60000]" : ""}`}>
                  {r.indice_retorno_30d_pct}%
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function Fechamentos() {
  const { user } = useAuth();
  const [filtros, setFiltros] = useState({
    periodo_inicio: "", periodo_fim: "",
    cidade: "", estado: "", tipo_servico: "", status_pagamento: "",
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = Object.entries(filtros).filter(([_, v]) => v).map(([k, v]) => `${k}=${encodeURIComponent(v)}`).join("&");
    api.get(`/relatorios/fechamentos${params ? "?" + params : ""}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [filtros]);

  useEffect(load, [load]);

  const podeVerPIX = user?.role === "admin" || user?.role === "empresa";

  return (
    <>
      <div className="bg-white border border-border p-4 mb-4 grid grid-cols-2 md:grid-cols-6 gap-3">
        <FF label="Início" type="date" v={filtros.periodo_inicio} on={v => setFiltros(f => ({...f, periodo_inicio: v}))} testid="rel-fech-inicio" />
        <FF label="Fim" type="date" v={filtros.periodo_fim} on={v => setFiltros(f => ({...f, periodo_fim: v}))} testid="rel-fech-fim" />
        <FF label="Cidade" v={filtros.cidade} on={v => setFiltros(f => ({...f, cidade: v}))} testid="rel-fech-cidade" />
        <FF label="Estado" v={filtros.estado} on={v => setFiltros(f => ({...f, estado: v}))} testid="rel-fech-estado" />
        <div>
          <Label className="label-cap">Tipo</Label>
          <Select value={filtros.tipo_servico} onValueChange={v => setFiltros(f => ({...f, tipo_servico: v === "_todos" ? "" : v}))}>
            <SelectTrigger className="mt-2" data-testid="rel-fech-tipo"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_todos">Todos</SelectItem>
              <SelectItem value="montagem">Montagem</SelectItem>
              <SelectItem value="assistencia">Assistência</SelectItem>
              <SelectItem value="revisao">Revisão</SelectItem>
              <SelectItem value="desmontagem">Desmontagem</SelectItem>
              <SelectItem value="transferencia">Transferência</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-cap">Pagamento</Label>
          <Select value={filtros.status_pagamento} onValueChange={v => setFiltros(f => ({...f, status_pagamento: v === "_todos" ? "" : v}))}>
            <SelectTrigger className="mt-2" data-testid="rel-fech-pago"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_todos">Todos</SelectItem>
              <SelectItem value="pago">Pago</SelectItem>
              <SelectItem value="pendente">Pendente</SelectItem>
              <SelectItem value="atrasado">Atrasado</SelectItem>
              <SelectItem value="cancelado">Cancelado</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-between mb-3">
        <div className="text-xs text-muted-foreground">{data ? `${data.total} fechamento(s)` : "—"}</div>
        <Button variant="outline" className="btn-sharp text-xs"
          onClick={() => exportCsv(`fechamentos.csv`, data?.linhas || [])}
          disabled={!data?.linhas?.length}
          data-testid="rel-fech-export">Exportar CSV</Button>
      </div>

      <div className="border border-border bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr>
              <Th>PIM</Th><Th>Cliente</Th><Th>Cidade</Th><Th>Tipo</Th>
              <Th>Data</Th><Th>Valor</Th><Th>Montador</Th>
              {podeVerPIX && <Th>PIX</Th>}
              <Th>Pagamento</Th><Th>Prev.</Th><Th>Efetiva</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">Carregando…</td></tr>
            ) : !data?.linhas?.length ? (
              <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">Sem fechamentos no filtro.</td></tr>
            ) : data.linhas.map(l => (
              <tr key={l.servico_id} className="border-t border-border hover:bg-secondary/30" data-testid={`fech-${l.servico_id}`}>
                <Td className="font-mono text-xs">{l.pim_code}</Td>
                <Td>{l.cliente_nome}</Td>
                <Td className="text-xs">{l.cliente_cidade}/{l.cliente_estado}</Td>
                <Td className="text-xs">{l.tipo}</Td>
                <Td>{fmtDate(l.data_execucao)}</Td>
                <Td className="font-mono">{fmtMoney(l.valor_servico)}</Td>
                <Td>{l.montador_nome || "—"}</Td>
                {podeVerPIX && <Td className="text-xs font-mono">{l.montador_pix || "—"}</Td>}
                <Td><StatusPagamento s={l.situacao_pagamento} /></Td>
                <Td>{fmtDate(l.data_prevista_pagamento)}</Td>
                <Td>{fmtDate(l.data_efetiva_pagamento)}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function FF({ label, v, on, testid, type="text" }) {
  return (
    <div>
      <Label className="label-cap">{label}</Label>
      <Input type={type} value={v} onChange={(e) => on(e.target.value)} className="mt-2" data-testid={testid} />
    </div>
  );
}
function Th({ children }) { return <th className="p-3 label-cap text-[10px] font-semibold">{children}</th>; }
function Td({ children, className="" }) { return <td className={`p-3 ${className}`}>{children}</td>; }
